import React, { useCallback, useEffect, useMemo, useState } from "react";
import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import Counter from "yet-another-react-lightbox/plugins/counter";
import Fullscreen from "yet-another-react-lightbox/plugins/fullscreen";
import Download from "yet-another-react-lightbox/plugins/download";
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";
import "yet-another-react-lightbox/plugins/counter.css";
import "./image-viewer.css";

function RotateIcon() {
  return (
    <svg
      className="yarl__icon"
      viewBox="0 0 24 24"
      width="22"
      height="22"
      aria-hidden="true"
      focusable="false"
    >
      <path
        fill="currentColor"
        d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46C19.54 15.03 20 13.57 20 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74C4.46 8.97 4 10.43 4 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z"
      />
    </svg>
  );
}

function ImageViewer({
  openLightbox,
  setOpenLightbox,
  lightboxSrcs,
  activeIndex = 0,
  onIndexChange,
}) {
  const [rotation, setRotation] = useState(0);

  const slides = useMemo(
    () =>
      (lightboxSrcs || [])
        .filter((item) => item?.src)
        .map((item, index) => {
          const fileName =
            item.name ||
            item.src?.split("/").pop()?.split("?")[0] ||
            `image-${index + 1}`;

          return {
            src: item.src,
            alt: item.alt || decodeURIComponent(fileName),
            download: {
              url: item.src,
              filename: decodeURIComponent(fileName),
            },
          };
        }),
    [lightboxSrcs],
  );

  const hasMultiple = slides.length > 1;

  const plugins = useMemo(() => {
    const list = [Zoom, Fullscreen, Download];
    if (hasMultiple) {
      list.unshift(Counter);
      list.push(Thumbnails);
    }
    return list;
  }, [hasMultiple]);

  const safeIndex =
    slides.length === 0
      ? 0
      : Math.min(Math.max(activeIndex, 0), slides.length - 1);

  const handleClose = useCallback(() => {
    setOpenLightbox(false);
    setRotation(0);
  }, [setOpenLightbox]);

  const handleRotate = useCallback(() => {
    setRotation((prev) => (prev + 90) % 360);
  }, []);

  useEffect(() => {
    if (!openLightbox) {
      setRotation(0);
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === "r" || event.key === "R") {
        const tag = event.target?.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA") return;
        event.preventDefault();
        setRotation((prev) => (prev + 90) % 360);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [openLightbox]);

  return (
    <Lightbox
      open={openLightbox}
      close={handleClose}
      index={safeIndex}
      slides={slides}
      plugins={plugins}
      className={`bh-lightbox bh-rotate-${rotation}`}
      controller={{
        closeOnBackdropClick: true,
        closeOnPullDown: true,
        disableSwipeNavigation: false,
      }}
      animation={{
        fade: 250,
        swipe: 280,
        navigation: 280,
      }}
      carousel={{
        finite: false,
        preload: 2,
        padding: "5%",
        spacing: "16px",
        imageFit: "contain",
      }}
      zoom={{
        maxZoomPixelRatio: 4,
        scrollToZoom: true,
        doubleClickDelay: 300,
        doubleClickMaxStops: 2,
        keyboardMove: true,
      }}
      thumbnails={{
        position: "bottom",
        width: 78,
        height: 58,
        border: 0,
        borderRadius: 10,
        padding: 10,
        gap: 10,
        imageFit: "cover",
        vignette: true,
        showToggle: true,
      }}
      counter={{
        separator: " / ",
        container: {
          className: "bh-lightbox-counter",
        },
      }}
      toolbar={{
        buttons: [
          <button
            key="rotate"
            type="button"
            className="yarl__button bh-lightbox-rotate-btn"
            title="Rotate image (R)"
            aria-label="Rotate image"
            onClick={handleRotate}
          >
            <RotateIcon />
          </button>,
          "download",
          "zoom",
          "fullscreen",
          "close",
        ],
      }}
      styles={{
        container: {
          backgroundColor: "rgba(8, 10, 14, 0.92)",
          backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(10px)",
          "--bh-lightbox-rotate": `${rotation}deg`,
        },
      }}
      render={{
        buttonPrev: hasMultiple ? undefined : () => null,
        buttonNext: hasMultiple ? undefined : () => null,
        iconClose: () => (
          <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
            <path
              fill="currentColor"
              d="M18.3 5.7a1 1 0 0 0-1.4 0L12 10.6 7.1 5.7a1 1 0 1 0-1.4 1.4l4.9 4.9-4.9 4.9a1 1 0 1 0 1.4 1.4l4.9-4.9 4.9 4.9a1 1 0 0 0 1.4-1.4L13.4 12l4.9-4.9a1 1 0 0 0 0-1.4z"
            />
          </svg>
        ),
      }}
      on={{
        view: ({ index }) => {
          setRotation(0);
          if (typeof onIndexChange === "function") {
            onIndexChange(index);
          }
        },
      }}
      labels={{
        Next: "Next image",
        Previous: "Previous image",
        Close: "Close",
        Download: "Download image",
        ZoomIn: "Zoom in",
        ZoomOut: "Zoom out",
        EnterFullscreen: "Enter fullscreen",
        ExitFullscreen: "Exit fullscreen",
        ShowThumbnails: "Show thumbnails",
        HideThumbnails: "Hide thumbnails",
        Rotate: "Rotate image",
      }}
    />
  );
}

export default ImageViewer;
